import mime from 'mime-types';
import JSZip from 'jszip';
import axios from 'axios';

/*
Extracts the user_id from the S3 url
 */
export const extractUserIdFromS3Url = (url) => {
	const urlSplit = url.split("/")
	const filename = urlSplit.pop() // remove filename
	const userIdStr = urlSplit.pop() // remove user_id
	return userIdStr.includes("user") ? parseInt(userIdStr.split("_").pop()) : parseInt(userIdStr)
}

/*
downloadFileBlob downloads a file from a signed url and returns a blob
 */
export const downloadFileBlob = async (url) => {
	const res = await fetch(url);
	const blob = new Blob([await res.blob()])
	return blob
}

/*
downloadFileBase64 downloads a file from a signed url and returns a base64 string
 */
export const downloadFileBase64 = async (url) => {
		const blob = await downloadFileBlob(url);
    const img = new Image();
    // Load the Blob data into the Image element
    img.src = URL.createObjectURL(blob);
    // Wait for the image to load (optional, but recommended)
    await new Promise((resolve) => (img.onload = resolve));
    // Create a canvas to draw the image
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    // Get the data-URL formatted image (PNG format)
    const dataURL = canvas.toDataURL("image/png");
		// Remove the canvas
		canvas.remove();
		return dataURL;
}

export const FORMATS_TO_SHOW_IN_TAB = [
	"jpg",
	"jpeg",
	"png",
	"gif",
	"bmp",
	"pdf",
	"jfif",
]

/*
downloadFile downloads a file from an aws signed url and saves it with the filename
 */
export const downloadFile = async (url, filename) => {
	const blob = await downloadFileBlob(url);
	const fileDownloaded = new File([blob], filename, { type: mime.lookup(filename) });
	const nurl = window.URL.createObjectURL(fileDownloaded);
	const a = document.createElement('a');
	a.style.display = 'none';
	a.href = nurl;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	window.URL.revokeObjectURL(nurl);
	a.remove();
}

/*
downloadFileAndShowInTab downloads a file from a signed url and shows it in a new tab
of the browser. @TODO: this should be fixed to make so that the file open in the new tab is named correctly with the filename instead of the random name generated by the browser blob
 */
export const downloadFileAndShowInTab = async (url, filename) => {
	if (FORMATS_TO_SHOW_IN_TAB.includes(re.exec(filename)[1])) {
		const blob = await downloadFileBlob(url);
		const fileDownloaded = new File([blob], filename, {type: mime.lookup(filename)});
		const nurl = window.URL.createObjectURL(fileDownloaded);
		window.open(nurl, '_blank');
	} else {
		downloadFile(url, filename);
	}
}

/*
getSignedUrl gets a signed url from the backend
 */
export const getSignedUrl = async (apiRequest, s3_url, filename, bucket) => {
	const signed_url = await apiRequest.post(`/aws-signed-url`, {
		content_type: '',
		format: '',
		name: filename,
		prefix: s3_url.split(".com/").pop(),
		bucket: bucket,
		method: 'GET'
	});
	return signed_url;
}

/*
downloadZip downloads a zip file with the files in the files array
 */
export const downloadZip = async (zipname, files) => {
	if (files.length === 1) {
		if (files[0]) {
			const file = files[0];
			const a = document.createElement('a');
			a.href = URL.createObjectURL(file.blob);
			a.download = file.filename;
			a.click();
			a.remove()
		} else{
			alert("No file to download!");
		}
	} else {
		const zip = new JSZip();
		const added_files = []
		for (let {filename, blob} of files.filter((f) => !!f?.blob)) {
			if (added_files.includes(filename)) {
				// avoid duplicate filenames
				const extension = filename.split(".").pop();
				const name = filename.replace(`.${extension}`, "");
				let i = 1;
				while (added_files.includes(filename)) {
					filename = `${name} (${i}).${extension}`;
					i++;
				}
			}
			zip.file(filename, blob);
			added_files.push(filename)
		}

		if (!added_files.length) {
			alert("No files to download!");
			return;
		}

		const zipBlob = await zip.generateAsync({type: 'blob'});
		// download zip file
		const element = document.createElement("a");
		const url = URL.createObjectURL(zipBlob);
		element.href = url;
		element.download = zipname + ".zip";
		document.body.appendChild(element); // Required for this to work in FireFox
		element.click();
		// cleanup
		URL.revokeObjectURL(url);
		document.body.removeChild(element);
	}
}

const re = /(?:\.([^.]+))?$/;

export const ACCEPTED_FORMATS = [
  "image/*",
  "application/pdf",
  ".xls",
  ".xlsx",
  ".doc",
  ".docx",
  ".ppt",
  ".pptx",
];

export const uploadFileToS3 = async (apiRequest, file, prefix, bucket, use_name=false, custom_return=false) => {
	let data = {
		content_type: file.type,
		format: re.exec(file.name)[1],
		prefix: prefix,
		bucket: bucket,
		method: "POST",
		name: file.name,
		use_name: use_name,
	};

	const {url, fields} = await apiRequest.post(`/aws-signed-url`, data);
	const formData = new FormData();
	Object.keys(fields).map((key) => {
		return formData.append(key, fields[key]);
	});
	// Actual file has to be appended last.
	formData.append("file", file);
	const options = {
		headers: {
			"Content-Type": "multipart/form-data",
			"Access-Control-Allow-Origin": "*",
			"Cache-Control": "max-age=31536000",
		},
		onUploadProgress: (progressEvent) => {},
	};
	await axios.post(url, formData, options);

	if(custom_return)
		return fields;

	return url + fields.key;
}

/**
 * Delete a file from S3
 * @param {*} apiRequest 
 * @param {string} s3_url 
 * @param {string} filename 
 * @param {string} bucket 
 * @returns 
 */
export const deleteFileFromS3 = async (apiRequest, prefix, bucket, link, encoded_filename=false) => {
	let data = {
		prefix,
		bucket,
		link,
		encoded_filename
	};

	await apiRequest.post(`/aws-delete-s3-file`, data);
}


export function formatBytes(bytes, dm = 2) {
  if (bytes === 0)
		return '0 B';
  const k = 1024;
	const sizes = [`${bytes <= 1 ? "B" : "B"}`, 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	const value = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));
  return value + ' ' + sizes[i];
}

/*
	prepareAndDownloadZip is a function that prepares the zip file and downloads it
	parameters:
	- documents: an array of documents
	make parallel requests to:  1)get the signed url of each document 2)download the document
	then create the zip file and download it
 */
export const prepareAndDownloadZipOfS3Files = async (docs, zipname, apiRequest) => {
	const files = await Promise.all(docs.map(async (doc) => {
		const signed_url = await getSignedUrl(apiRequest, doc.s3_url, doc.name, doc.bucket);
		const blob = await downloadFileBlob(signed_url);
		return {filename: doc.name, blob: blob};
	}));
	return await downloadZip(zipname, files)
}
